/**
 * Credenziali master: rinnovo JWT (single-flight, via BFF così anche il cookie
 * httpOnly `session_token_master` viene ri-impostato), scadenza e pulizia di
 * logout. Esiti e corse (transitorio vs definitivo, rotazione cross-tab,
 * risposta tardiva a sessione finita) sono gestiti qui. NESSUN tracker di attività né scheduler qui: la sessione per
 * inattività vive nel core condiviso `session-idle` di `@easyflow/ui-frontend`
 * (avviato da `AuthProvider`), che chiama `refreshToken` quando serve.
 */
import { refreshTokenResponseDataSchema, type RefreshTokenResponseData } from '@easyflow/shared';
import { getSessionIdleStore, sessionPolicyToConfig } from '@easyflow/ui-frontend';
import apiClient from './api';
import { AUTH_STORAGE_KEYS, clearAllAuthStorage, storeSessionPolicy } from '@/constants/auth';

const FETCH_TIMEOUT_MS = 5 * 1000;
const REFRESH_TIMEOUT_MS = 10 * 1000;

/** Evento emesso dopo un rinnovo riuscito (`detail.token` = nuovo access token). */
export const TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';
/** Evento emesso quando le credenziali non sono più rinnovabili (refresh token respinto). */
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

export interface TokenRefreshedDetail {
  readonly token: string;
}

function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number = FETCH_TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
}

function revokeRefreshToken(refreshTokenValue: string): Promise<Response> {
  return fetchWithTimeout('/api/master/logout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: refreshTokenValue }),
  });
}

function clearBffCookie(): Promise<Response> {
  return fetchWithTimeout('/api/bff/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Auth-Scope': 'master' },
  });
}

/** Applica un payload di refresh valido: storage, client API, policy del core, evento. */
function adoptRefreshedCredentials(data: RefreshTokenResponseData): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, data.token);
  if (data.refreshToken) {
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
  }
  storeSessionPolicy(data.sessionPolicy);
  apiClient.setToken(data.token);
  // Durate aggiornate a caldo: un cambio di policy dal Security Center si
  // applica al prossimo refresh senza re-login.
  getSessionIdleStore('master').updateConfig(sessionPolicyToConfig(data.sessionPolicy));
  window.dispatchEvent(new CustomEvent<TokenRefreshedDetail>(TOKEN_REFRESHED_EVENT, { detail: { token: data.token } }));
}

/** Esito del rinnovo: i chiamanti distinguono transitorio da definitivo. */
export type RefreshOutcome =
  /** Nuovo access token adottato (o già adottato da un'altra tab). */
  | { readonly status: 'refreshed'; readonly token: string }
  /** Rete/timeout/5xx/payload non valido: credenziali intatte, riprovare più tardi. */
  | { readonly status: 'unavailable' }
  /** Refresh token respinto o sessione già terminata: credenziali locali chiuse. */
  | { readonly status: 'rejected' };

/**
 * Attesa della rotazione cross-tab dopo un 401: il backend respinge la tab
 * perdente durante la finestra di grazia mentre la vincente sta ancora
 * ricevendo la risposta; entro questo tempo le credenziali più nuove compaiono
 * in storage e vengono adottate invece di chiudere la sessione.
 */
const CROSS_TAB_ROTATION_WAIT_MS = 2_000;

/**
 * Generazione della sessione locale: incrementata da logout/scadenza. Una
 * risposta di refresh arrivata DOPO la fine della sessione viene scartata
 * (altrimenti riscriverebbe storage e cookie BFF dopo la pulizia).
 */
let sessionGeneration = 0;

interface StoredCredentials {
  readonly accessToken: string;
  readonly refreshToken: string;
}

function readStoredCredentials(): StoredCredentials | null {
  const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  const refreshTokenValue = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  return accessToken && refreshTokenValue ? { accessToken, refreshToken: refreshTokenValue } : null;
}

/** Credenziali ruotate da un'altra tab (refresh token diverso da quello inviato), se presenti. */
function rotatedCredentials(sentRefreshToken: string): StoredCredentials | null {
  const stored = readStoredCredentials();
  return stored && stored.refreshToken !== sentRefreshToken ? stored : null;
}

function waitForCrossTabRotation(sentRefreshToken: string): Promise<StoredCredentials | null> {
  const immediate = rotatedCredentials(sentRefreshToken);
  if (immediate) return Promise.resolve(immediate);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: StoredCredentials | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
      resolve(value);
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== AUTH_STORAGE_KEYS.REFRESH_TOKEN && event.key !== AUTH_STORAGE_KEYS.TOKEN && event.key !== null) return;
      // Altra tab ha chiuso la sessione: niente da adottare.
      if (localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) === null) {
        finish(null);
        return;
      }
      const rotated = rotatedCredentials(sentRefreshToken);
      if (rotated) finish(rotated);
    };
    const timer = setTimeout(() => finish(rotatedCredentials(sentRefreshToken)), CROSS_TAB_ROTATION_WAIT_MS);
    window.addEventListener('storage', onStorage);
  });
}

/** Adotta credenziali già scritte in storage da un'altra tab (nessuna riscrittura). */
function adoptRotatedCredentials(rotated: StoredCredentials): RefreshOutcome {
  apiClient.setToken(rotated.accessToken);
  window.dispatchEvent(new CustomEvent<TokenRefreshedDetail>(TOKEN_REFRESHED_EVENT, { detail: { token: rotated.accessToken } }));
  return { status: 'refreshed', token: rotated.accessToken };
}

let inFlightRefresh: Promise<RefreshOutcome> | null = null;

/**
 * Rinnova l'access token master (e il cookie BFF). Single-flight: N chiamate
 * concorrenti condividono la stessa richiesta. Se il refresh token è stato
 * respinto in modo definitivo (e nessun'altra tab lo ha ruotato nel
 * frattempo), la sessione locale viene chiusa ed emesso `SESSION_EXPIRED_EVENT`.
 */
export function refreshToken(): Promise<RefreshOutcome> {
  if (inFlightRefresh) return inFlightRefresh;
  const run = doRefresh().finally(() => {
    if (inFlightRefresh === run) inFlightRefresh = null;
  });
  inFlightRefresh = run;
  return run;
}

async function doRefresh(): Promise<RefreshOutcome> {
  const currentRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (!currentRefreshToken) return { status: 'rejected' };
  const generation = sessionGeneration;

  let response: Response;
  try {
    response = await fetchWithTimeout('/api/bff/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Scope': 'master' },
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    }, REFRESH_TIMEOUT_MS);
  } catch {
    // Rete/timeout: transitorio, nessuna decisione sulla sessione.
    return { status: 'unavailable' };
  }

  if (generation !== sessionGeneration) {
    // Sessione terminata (logout/scadenza) mentre la richiesta era in volo:
    // il cookie BFF è appena stato ri-impostato, va cancellato di nuovo, e il
    // nuovo refresh token (se emesso) revocato.
    await discardLateRefresh(response);
    return { status: 'rejected' };
  }

  if (response.status === 401 || response.status === 403) {
    const rotated = await waitForCrossTabRotation(currentRefreshToken);
    if (generation !== sessionGeneration) return { status: 'rejected' };
    if (rotated) return adoptRotatedCredentials(rotated);
    if (localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) === null) {
      // Un'altra tab ha già chiuso la sessione.
      return { status: 'rejected' };
    }
    expireSession({ revoke: false });
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    return { status: 'rejected' };
  }

  if (!response.ok) return { status: 'unavailable' };

  const data = await parseRefreshPayload(response);
  if (!data) return { status: 'unavailable' };
  if (generation !== sessionGeneration) {
    await discardLateRefresh(data);
    return { status: 'rejected' };
  }

  const rotated = rotatedCredentials(currentRefreshToken);
  if (rotated) {
    // Un'altra tab ha già portato avanti la catena: le sue credenziali sono
    // più recenti delle nostre, che vengono scartate.
    return adoptRotatedCredentials(rotated);
  }

  adoptRefreshedCredentials(data);
  return { status: 'refreshed', token: data.token };
}

async function parseRefreshPayload(response: Response): Promise<RefreshTokenResponseData | null> {
  const body: unknown = await response.json().catch((): null => null);
  const data = typeof body === 'object' && body !== null && 'data' in body
    ? (body as { data: unknown }).data
    : undefined;
  const parsed = refreshTokenResponseDataSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

/** Risposta di refresh arrivata a sessione finita: annulla i suoi effetti server-side. */
async function discardLateRefresh(source: Response | RefreshTokenResponseData): Promise<void> {
  const data = source instanceof Response
    ? (source.ok ? await parseRefreshPayload(source) : null)
    : source;
  if (data?.refreshToken) {
    await revokeRefreshToken(data.refreshToken).catch(() => undefined);
  }
  await clearBffCookie().catch(() => undefined);
}

export interface ExpireSessionOptions {
  /** Revoca il refresh token lato server (solo la tab initiator della scadenza). */
  readonly revoke: boolean;
}

/**
 * Chiude la sessione locale: storage pulito, client API senza token, cookie
 * BFF cancellato e (se `revoke`) refresh token revocato. Idempotente.
 */
export function expireSession(options: ExpireSessionOptions): void {
  sessionGeneration += 1;
  const refreshTokenValue = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  clearAllAuthStorage();
  apiClient.clearToken();
  if (options.revoke && refreshTokenValue) {
    revokeRefreshToken(refreshTokenValue).catch(() => undefined);
  }
  clearBffCookie().catch(() => undefined);
}

/** Logout esplicito: revoca refresh token e cancella il cookie BFF (attende entrambi). */
export async function performLogoutCleanup(): Promise<void> {
  sessionGeneration += 1;
  const refreshTokenValue = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  if (refreshTokenValue) {
    try {
      await revokeRefreshToken(refreshTokenValue);
    } catch {
      // best-effort
    }
  }
  try {
    await clearBffCookie();
  } catch {
    // best-effort
  }
}
